<template>
  <section class="block mx-auto mt-32 max-w-448">
    <SitterAlertBanner v-if="serverError" :error="serverError" />
    <PoppinsCard>
      <CareTypeFormHeader careType="housekeeping" />
      <form id="create-profile-housekeeping" novalidate @submit.prevent="handleSubmit">
        <div v-for="(field, index) in formFields" :key="index">
          <component :is="field.component" ref="childComponents" v-bind="field.props" />
        </div>

        <PoppinsButton
          id="formSubmit"
          :loading="loading"
          class="w-full my-8"
          variant="primary"
          value="Save & Continue"
          size="medium"
          type="submit"
        />
      </form>
    </PoppinsCard>
  </section>
</template>

<script lang="ts">
import currency from 'currency.js';
import { UPSERT_PROFILE_SITTER } from '@/graphql/profile/mutations';
import SitterAlertBanner from '@/apps/sitter/signup/account/components/SitterAlertBanner.vue';
import CareTypeFormHeader from '../components/CareTypeFormHeader.vue';
import YearsExperienceField from '../components/YearsExperienceField.vue';
import ExperienceCheckbox from '../components/ExperienceCheckbox.vue';
import MaxDistanceSelect from '../components/MaxDistanceSelect.vue';
import HousekeepingServicesCheckbox from '../components/HousekeepingServicesCheckbox.vue';
import ComfortablePetsRadio from '../components/ComfortablePetsRadio.vue';
import PaymentRateFields from '../components/PaymentRateFields.vue';
import BioArea from '../components/BioArea.vue';
import Button from '@/components/button/Button.vue';
import Card from '@/components/card/Card.vue';
import { housekeepingExperienceOptions } from '../components/__types__/ExperienceTypes';
import { navigateToRoute } from '@/lib/sitterOnboarding/sitterOnboardingState';

export default {
  name: 'ProviderHousekeepingForm',
  components: {
    SitterAlertBanner,
    CareTypeFormHeader,
    YearsExperienceField,
    ExperienceCheckbox,
    MaxDistanceSelect,
    HousekeepingServicesCheckbox,
    ComfortablePetsRadio,
    PaymentRateFields,
    BioArea,
    PoppinsCard: Card,
    PoppinsButton: Button,
  },
  data(): any {
    return {
      formFields: [
        { component: 'YearsExperienceField' },
        {
          component: 'ExperienceCheckbox',
          props: {
            experienceTypes: housekeepingExperienceOptions,
            generateScreenReaderLabel: (optionText) => `I have paid experience with ${optionText}`,
            legend: 'What types of services do you have experience providing?',
          },
        },
        { component: 'MaxDistanceSelect' },
        { component: 'HousekeepingServicesCheckbox' },
        { component: 'ComfortablePetsRadio' },
        { component: 'PaymentRateFields' },
        { component: 'BioArea' },
      ],
      serverError: null,
      loading: false,
    };
  },
  computed: {
    housekeepingServices() {
      const services = this.$store.getters.getFormField('services');
      // this hack fixes a bug with PoppinsCheckbox (ch166978)
      // without this, experience component's "running errands" checkbox will cancel out services "errands" checkbox
      return services.includes('SERVICE_ERRANDS') ? services.map((s) => (s === 'SERVICE_ERRANDS' ? 'ERRANDS' : s)) : services;
    },
    firstInvalidComponent() {
      return this.$refs.childComponents.find((childComponent) => {
        return childComponent.validate && !childComponent.validate();
      });
    },
  },
  methods: {
    handleSubmit() {
      this.$refs.childComponents.forEach((childComponent) => {
        if (childComponent.validate) {
          childComponent.validate();
        }
      });

      return this.firstInvalidComponent ? this.sendFocusToError() : this.upsertProfile();
    },
    sendFocusToError() {
      this.firstInvalidComponent.focusElement?.();
    },
    async upsertProfile() {
      try {
        await this.$apollo.mutate({
          mutation: UPSERT_PROFILE_SITTER,
          variables: {
            profile: {
              housekeeping: {
                yearsExperience: Number(this.$store.getters.getFormField('yearsExperience')),
                experience: this.$store.getters.getFormField('experience'),
                distanceWillingToTravel: Number(this.$store.getters.getFormField('distanceWillingToTravel')),
                services: this.housekeepingServices,
                comfortableWithPets: this.$store.getters.getFormField('comfortableWithPets') === 'true',
                paymentRate: {
                  min: currency(this.$store.getters.minPaymentRate).intValue,
                  max: currency(this.$store.getters.maxPaymentRate).intValue,
                },
                bio: this.$store.getters.getFormField('bio'),
              },
            },
          },
        });
        this.loading = true;
        navigateToRoute('/sitter/verify_identity', this.$router);
      } catch (error) {
        console.error(error);
        this.serverError = new Error('unknown-error');
      }
    },
  },
};
</script>
